import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Copy,
  MoreHorizontal,
  Pencil,
  Power,
  RefreshCcw,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { formatDate, timeAgo } from '../lib/format';
import {
  FormEditDrawer,
  ModerationChip,
  SubmissionModerationDrawer,
  FEEDBACK_AXES_ORDER,
  FEEDBACK_AXIS_LABEL,
  formatPeriodLabel,
  formatScore,
  type FeedbackAdminAggregatesResponse,
  type FeedbackAdminListResponse,
  type FeedbackAdminSubmission,
  type FeedbackFormDetailResponse,
  type FeedbackFormRow,
  type FeedbackFormsResponse,
  type FeedbackFormStatus,
  type FeedbackQuestion,
  type FormEditDrawerMode,
} from '../components/feedback';

// -------------------------------------------------------------------------
// Route
// -------------------------------------------------------------------------

export const Route = createFileRoute('/_authed/hr/feedback/forms/$formId')({
  component: FormDetailPage,
});

// -------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------

type Tab = 'questions' | 'settings' | 'submissions' | 'aggregates' | 'activity';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'questions', label: 'Questions' },
  { value: 'settings', label: 'Settings' },
  { value: 'submissions', label: 'Submissions' },
  { value: 'aggregates', label: 'Aggregates' },
  { value: 'activity', label: 'Activity' },
];

const STATUS_TONE: Record<FeedbackFormStatus, string> = {
  active:
    'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]',
  scheduled:
    'border-gold-500/40 bg-gold-500/10 text-gold-700 dark:text-gold-100',
  inactive:
    'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
  expired:
    'border-[color:var(--color-danger)]/30 bg-[color:var(--color-danger)]/5 text-[color:var(--color-danger)]',
};

const STATUS_LABEL: Record<FeedbackFormStatus, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  inactive: 'Inactive',
  expired: 'Expired',
};

function FormDetailPage(): JSX.Element {
  const { formId } = Route.useParams();
  const hasRole = useAuthStore((s) => s.hasRole);
  const canManage = hasRole('super_admin', 'academic_admin');
  const isSuperAdmin = hasRole('super_admin');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [tab, setTab] = useState<Tab>('questions');
  const [drawer, setDrawer] = useState<FormEditDrawerMode | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [drawerSubmission, setDrawerSubmission] =
    useState<FeedbackAdminSubmission | null>(null);

  const detailQuery = useQuery({
    queryKey: ['feedback', 'form', 'detail', formId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackFormDetailResponse>(
        `/api/feedback/forms/${encodeURIComponent(formId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Listing endpoint gives us the createdAt-sorted forms so we can derive
  // the version rank without a dedicated server field.
  const formsQuery = useQuery({
    queryKey: ['feedback', 'forms'] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackFormsResponse>('/api/feedback/forms', { signal }),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Submissions list — also feeds the "last submission" KPI and active
  // period chip when present.
  const submissionsQuery = useQuery({
    queryKey: ['feedback', 'admin', 'submissions', 'by-form', formId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminListResponse>(
        '/api/feedback/admin/submissions',
        {
          signal,
          query: { formId, page: 1, pageSize: 100 },
        },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const aggregatesQuery = useQuery({
    queryKey: ['feedback', 'admin', 'aggregates', 'by-form', formId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminAggregatesResponse>(
        '/api/feedback/admin/aggregates',
        {
          signal,
          query: { formId },
        },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const detail = detailQuery.data;
  const formRow = useMemo(
    () => buildFormRow(detail),
    [detail],
  );

  const versionRank = useMemo(() => {
    if (!detail || !formsQuery.data) return null;
    const sorted = [...formsQuery.data.items].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    const idx = sorted.findIndex((row) => row.id === detail.id);
    return idx >= 0 ? idx + 1 : null;
  }, [detail, formsQuery.data]);

  const lastSubmission = useMemo(() => {
    const items = submissionsQuery.data?.items ?? [];
    if (items.length === 0) {
      return { at: null as string | null, periodKey: null as string | null };
    }
    const sorted = [...items].sort((a, b) =>
      b.submittedAt.localeCompare(a.submittedAt),
    );
    const newest = sorted[0]!;
    return { at: newest.submittedAt, periodKey: newest.periodKey };
  }, [submissionsQuery.data]);

  const submissionCount =
    detail?.submissionCount ?? submissionsQuery.data?.total ?? 0;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['feedback', 'form', 'detail', formId],
    });
    void queryClient.invalidateQueries({ queryKey: ['feedback', 'forms'] });
    void queryClient.invalidateQueries({
      queryKey: ['feedback', 'admin', 'submissions', 'by-form', formId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['feedback', 'admin', 'aggregates', 'by-form', formId],
    });
  };

  const togglePatchMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      apiRequest<{ ok: true }>(
        `/api/feedback/forms/${encodeURIComponent(formId)}`,
        { method: 'PATCH', body: { isActive } },
      ),
    onSuccess: (_res, isActive) => {
      toast(isActive ? 'Form activated.' : 'Form deactivated.', {
        variant: 'success',
      });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not toggle form.',
        { variant: 'error' },
      ),
  });

  const recomputeMutation = useMutation({
    mutationFn: () =>
      apiRequest<{
        ok: true;
        scanned: number;
        recomputed: number;
        errors: number;
      }>('/api/feedback/admin/recompute', {
        method: 'POST',
        body: { formId },
      }),
    onSuccess: (res) => {
      toast(
        res.errors > 0
          ? `Recomputed ${res.recomputed} with ${res.errors} error${res.errors === 1 ? '' : 's'}.`
          : `Recomputed ${res.recomputed} teacher${res.recomputed === 1 ? '' : 's'} for this form.`,
        { variant: res.errors > 0 ? 'info' : 'success' },
      );
      void queryClient.invalidateQueries({ queryKey: ['feedback', 'admin'] });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not recompute.',
        { variant: 'error' },
      ),
  });

  const handleToggleActive = async (): Promise<void> => {
    if (!detail) return;
    setOverflowOpen(false);
    const next = !detail.isActive;
    const ok = await confirm({
      title: next ? 'Activate this form?' : 'Deactivate this form?',
      description: next
        ? 'Parents can submit responses against this form once active. Make sure another version is not already active for the same scope.'
        : 'Parents will no longer see this form. Existing submissions and aggregates are preserved.',
      confirmLabel: next ? 'Activate' : 'Deactivate',
      ...(next ? {} : { variant: 'danger' as const }),
    });
    if (!ok) return;
    togglePatchMutation.mutate(next);
  };

  const handleRecompute = async (): Promise<void> => {
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Recompute aggregates for this form?',
      description:
        'Rebuilds every aggregate row scoped to this form. Heavy admin action — runs synchronously and audits the trigger.',
      confirmLabel: 'Recompute',
    });
    if (!ok) return;
    recomputeMutation.mutate();
  };

  const versionLabel = versionRank !== null ? `v${versionRank}` : '';

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!detail) return [];
    return [
      {
        label: 'Submissions captured',
        value: submissionCount,
      },
      {
        label: 'Active period',
        value: lastSubmission.periodKey
          ? formatPeriodLabel(lastSubmission.periodKey)
          : '—',
        sub: lastSubmission.periodKey ? 'most recent' : 'no submissions',
      },
      {
        label: 'Visibility threshold',
        value: `${detail.minVisibleResponseCount}`,
        sub: 'min responses to share',
      },
      {
        label: 'Edit window',
        value: `${detail.editWindowHours}h`,
        sub: 'parent edit grace',
      },
      {
        label: 'Last submission',
        value: lastSubmission.at ? timeAgo(lastSubmission.at) : '—',
        sub: lastSubmission.at
          ? formatDate(lastSubmission.at, { withTime: true })
          : undefined,
      },
      {
        label: 'Aggregate rows',
        value: detail.aggregateCount,
      },
    ];
  }, [detail, lastSubmission, submissionCount]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Feedback form"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !detail ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={detail.title}
              sublabel={
                <>
                  <span>
                    {detail.scope} · {detail.cadence}
                  </span>
                  {versionLabel ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{versionLabel}</span>
                    </>
                  ) : null}
                </>
              }
              statusChip={
                <div className="flex flex-wrap gap-1.5">
                  <span
                    className={clsx(
                      'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
                      STATUS_TONE[detail.status],
                    )}
                  >
                    {STATUS_LABEL[detail.status]}
                  </span>
                  {submissionCount > 0 ? (
                    <span className="inline-flex items-center rounded-full border border-gold-500/40 bg-gold-500/10 px-2 py-0.5 text-xs font-medium text-gold-700">
                      In use
                    </span>
                  ) : null}
                </div>
              }
              backTo="/hr/feedback"
              actions={
                <>
                  {isSuperAdmin && formRow ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() =>
                        setDrawer({
                          kind: 'edit',
                          form: formRow,
                          hasSubmissions: submissionCount > 0,
                        })
                      }
                    >
                      Edit thresholds
                    </Button>
                  ) : null}
                  {isSuperAdmin ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Power size={14} aria-hidden="true" />}
                      loading={togglePatchMutation.isPending}
                      onClick={() => {
                        void handleToggleActive();
                      }}
                    >
                      {detail.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  ) : null}
                  {isSuperAdmin && formRow ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Copy size={14} aria-hidden="true" />}
                      onClick={() =>
                        setDrawer({ kind: 'clone', sourceForm: formRow })
                      }
                    >
                      Duplicate
                    </Button>
                  ) : null}
                  {isSuperAdmin ? (
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      items={[
                        {
                          label: 'Recompute aggregates for this form',
                          icon: <RefreshCcw size={14} aria-hidden="true" />,
                          onSelect: () => {
                            void handleRecompute();
                          },
                          disabled: recomputeMutation.isPending,
                        },
                      ]}
                    />
                  ) : null}
                </>
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Form sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TAB_OPTIONS.map((opt) => {
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'questions' ? (
              <QuestionsTab questionSet={parseQuestionSet(detail.questionSet)} />
            ) : null}
            {tab === 'settings' ? (
              <SettingsTab detail={detail} />
            ) : null}
            {tab === 'submissions' ? (
              <SubmissionsTab
                query={submissionsQuery}
                onModerate={(row) => setDrawerSubmission(row)}
              />
            ) : null}
            {tab === 'aggregates' ? (
              <AggregatesTab query={aggregatesQuery} />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="feedback_form"
                targetId={detail.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <FormEditDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          invalidate();
        }}
      />

      <SubmissionModerationDrawer
        submission={drawerSubmission}
        canManage={canManage}
        onClose={() => setDrawerSubmission(null)}
        onModerated={() => {
          setDrawerSubmission(null);
          invalidate();
        }}
      />

    </div>
  );
}

// -------------------------------------------------------------------------
// Tab bodies
// -------------------------------------------------------------------------

function QuestionsTab({
  questionSet,
}: {
  questionSet: FeedbackQuestion[];
}): JSX.Element {
  if (questionSet.length === 0) {
    return (
      <EmptyState
        title="No questions defined"
        description="The form's question_set is empty or could not be parsed. Use the Edit drawer to update it."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <table className="w-full text-sm">
        <thead className="bg-[color:var(--color-surface)]">
          <tr>
            <Th>Axis</Th>
            <Th>Prompt</Th>
            <Th align="right">Scale</Th>
            <Th>Labels</Th>
          </tr>
        </thead>
        <tbody>
          {questionSet.map((q) => (
            <tr
              key={q.axis}
              className="border-t border-[color:var(--color-border)] align-top"
            >
              <td className="px-4 py-2 text-sm font-medium">
                {FEEDBACK_AXIS_LABEL[q.axis] ?? q.axis}
              </td>
              <td className="px-4 py-2 text-sm text-[color:var(--color-text)]">
                {q.prompt}
              </td>
              <td className="px-4 py-2 text-right text-xs tabular-nums text-[color:var(--color-text-muted)]">
                {q.scaleMin}–{q.scaleMax}
              </td>
              <td className="px-4 py-2 text-xs text-[color:var(--color-text-muted)]">
                {q.scaleLabels.join(' · ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab({
  detail,
}: {
  detail: FeedbackFormDetailResponse;
}): JSX.Element {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <Card title="Lifecycle">
        <Row label="Status">{STATUS_LABEL[detail.status]}</Row>
        <Row label="Active">{detail.isActive ? 'Yes' : 'No'}</Row>
        <Row label="Active from">
          {detail.activeFrom ? formatDate(detail.activeFrom) : '—'}
        </Row>
        <Row label="Active to">
          {detail.activeTo ? formatDate(detail.activeTo) : '—'}
        </Row>
      </Card>
      <Card title="Aggregator policy">
        <Row label="Visibility threshold">
          {detail.minVisibleResponseCount} responses
        </Row>
        <Row label="Edit window">{detail.editWindowHours} hours</Row>
        <Row label="Cadence">{detail.cadence}</Row>
        <Row label="Scope">{detail.scope}</Row>
      </Card>
      <Card title="Audit trail anchors">
        <Row label="Created by">{detail.createdByName ?? detail.createdBy}</Row>
        <Row label="Created at">
          {formatDate(detail.createdAt, { withTime: true })}
        </Row>
        <Row label="Updated at">
          {formatDate(detail.updatedAt, { withTime: true })}
        </Row>
        <Row label="Form ID">
          <code className="font-mono text-xs">{detail.id}</code>
        </Row>
      </Card>
    </section>
  );
}

interface SubmissionsTabProps {
  query: ReturnType<typeof useQuery<FeedbackAdminListResponse, unknown>>;
  onModerate: (row: FeedbackAdminSubmission) => void;
}

function SubmissionsTab({
  query,
  onModerate,
}: SubmissionsTabProps): JSX.Element {
  const navigate = useNavigate();

  if (query.isLoading && !query.data) {
    return <Skeleton className="h-64 w-full" />;
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No submissions captured yet"
        description="Submissions against this form will appear here as parents respond."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <Th>Submitted</Th>
              <Th>Period</Th>
              <Th>Child</Th>
              <Th>Teacher</Th>
              <Th align="right">Overall</Th>
              <Th>Moderation</Th>
              <Th align="right">
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id}
                onClick={() =>
                  navigate({
                    to: '/hr/feedback/submissions/$submissionId',
                    params: { submissionId: row.id },
                  })
                }
                className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                  {formatDate(row.submittedAt, { withTime: true })}
                </td>
                <td className="px-4 py-2 align-middle text-xs">
                  {formatPeriodLabel(row.periodKey)}
                </td>
                <td className="px-4 py-2 align-middle">
                  <p className="font-medium">{row.studentName ?? '—'}</p>
                </td>
                <td className="px-4 py-2 align-middle">
                  <p className="font-medium">{row.teacherName ?? '—'}</p>
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums font-semibold">
                  {row.responses?.overall ?? '—'}
                  <span className="ml-0.5 text-[10px] text-[color:var(--color-text-muted)]">
                    /5
                  </span>
                </td>
                <td className="px-4 py-2 align-middle">
                  <ModerationChip status={row.moderationStatus} />
                </td>
                <td className="px-4 py-2 align-middle text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onModerate(row);
                    }}
                  >
                    Moderate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AggregatesTab({
  query,
}: {
  query: ReturnType<typeof useQuery<FeedbackAdminAggregatesResponse, unknown>>;
}): JSX.Element {
  const navigate = useNavigate();

  if (query.isLoading && !query.data) {
    return <Skeleton className="h-64 w-full" />;
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No aggregates yet"
        description="The aggregator produces a row once submissions exist. Use the overflow menu to trigger a recompute."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <Th>Teacher</Th>
              <Th>Period</Th>
              <Th align="right">Responses</Th>
              {FEEDBACK_AXES_ORDER.map((axis) => (
                <Th key={axis} align="right">
                  {FEEDBACK_AXIS_LABEL[axis]}
                </Th>
              ))}
              <Th>Visible</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id}
                onClick={() =>
                  navigate({
                    to: '/hr/feedback/teachers/$teacherId',
                    params: { teacherId: row.teacherId },
                    search: { periodKey: row.periodKey },
                  })
                }
                className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle">
                  <p className="font-medium">{row.teacherName ?? '—'}</p>
                </td>
                <td className="px-4 py-2 align-middle text-xs">
                  {formatPeriodLabel(row.periodKey)}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums">
                  {row.responsesCount}
                </td>
                {FEEDBACK_AXES_ORDER.map((axis) => {
                  const x100 = {
                    clarity: row.clarityAvgX100,
                    patience: row.patienceAvgX100,
                    communication: row.communicationAvgX100,
                    progress: row.progressAvgX100,
                    overall: row.overallAvgX100,
                  }[axis];
                  return (
                    <td
                      key={axis}
                      className="px-4 py-2 align-middle text-right tabular-nums"
                    >
                      {formatScore(x100)}
                    </td>
                  );
                })}
                <td className="px-4 py-2 align-middle text-xs">
                  {row.visibleToTeacher ? (
                    <span className="rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-[10px] uppercase text-[color:var(--color-success)]">
                      Visible
                    </span>
                  ) : (
                    <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] uppercase text-[color:var(--color-text-muted)]">
                      Hidden
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

/**
 * Coerce the form detail payload into a `FeedbackFormRow` shape so the
 * existing `FormEditDrawer` (which is typed against the row shape used by
 * the list endpoint) can consume it for both edit and clone modes.
 */
const buildFormRow = (
  detail: FeedbackFormDetailResponse | undefined,
): FeedbackFormRow | null => {
  if (!detail) return null;
  return {
    id: detail.id,
    title: detail.title,
    scope: detail.scope,
    cadence: detail.cadence,
    questionSet: parseQuestionSet(detail.questionSet),
    activeFrom: detail.activeFrom,
    activeTo: detail.activeTo,
    isActive: detail.isActive,
    minVisibleResponseCount: detail.minVisibleResponseCount,
    editWindowHours: detail.editWindowHours,
    createdBy: detail.createdBy,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
  };
};

/**
 * Validate the `unknown` questionSet from the wire into a typed array.
 * Bad/missing fields fall back to safe defaults rather than throwing so
 * the page still renders even when an admin edits the question_set into
 * an unexpected shape.
 */
const parseQuestionSet = (raw: unknown): FeedbackQuestion[] => {
  if (!Array.isArray(raw)) return [];
  const allowedAxes = new Set([
    'clarity',
    'patience',
    'communication',
    'progress',
    'overall',
  ] as const);
  const out: FeedbackQuestion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const axis = rec['axis'];
    if (typeof axis !== 'string' || !allowedAxes.has(axis as FeedbackQuestion['axis'])) {
      continue;
    }
    const prompt = typeof rec['prompt'] === 'string' ? rec['prompt'] : '';
    const scaleMin =
      typeof rec['scaleMin'] === 'number' ? rec['scaleMin'] : 1;
    const scaleMax =
      typeof rec['scaleMax'] === 'number' ? rec['scaleMax'] : 5;
    const labels = Array.isArray(rec['scaleLabels'])
      ? rec['scaleLabels'].filter(
          (l): l is string => typeof l === 'string',
        )
      : [];
    out.push({
      axis: axis as FeedbackQuestion['axis'],
      prompt,
      scaleMin,
      scaleMax,
      scaleLabels: labels,
    });
  }
  return out;
};

// -------------------------------------------------------------------------
// Primitives
// -------------------------------------------------------------------------

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}): JSX.Element {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <h3 className="font-display text-base font-semibold">{title}</h3>
      <dl className="mt-3 space-y-2 text-sm">{children}</dl>
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-3">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="col-span-2 text-[color:var(--color-text)]">{children}</dd>
    </div>
  );
}

interface OverflowItem {
  label: string;
  icon?: JSX.Element;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  items: OverflowItem[];
}

function OverflowMenu({
  open,
  onOpenChange,
  items,
}: OverflowMenuProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
      >
        More
      </Button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <ul
            role="menu"
            className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-lg"
          >
            {items.map((item) => (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={item.onSelect}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-[color:var(--color-surface)]',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    item.danger && 'text-[color:var(--color-danger)]',
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
